import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CloudArrowUp, CheckCircle, Database, ArrowsClockwise
} from '@phosphor-icons/react';

export default function CmsStorageManager() {
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    loadConfig();
    loadStats();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/storage-settings`, {
        withCredentials: true,
      });
      setConfig(r.data);
    } catch (e) {
      toast.error('Failed to load storage settings');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/storage-settings/stats`, {
        withCredentials: true,
      });
      setStats(r.data);
    } catch (e) {
      console.error('Failed to load stats');
    }
  };

  const saveConfig = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/storage-settings`,
        config,
        { withCredentials: true }
      );
      toast.success('Storage settings saved');
      loadConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/storage-settings/test`,
        {},
        { withCredentials: true }
      );
      toast.success(r.data.message);
    } catch (e) {
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-16"><div className="animate-spin h-8 w-8 border-b-2 border-primary rounded-full"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="cms-storage-manager">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <CloudArrowUp size={28} weight="duotone" className="text-primary" />
          Storage Provider Manager
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Configure and switch between storage providers (Emergent, S3, GCS, Azure)
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Total Files</p>
            <p className="text-2xl font-bold">{stats.total_files}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Storage Used (MB)</p>
            <p className="text-2xl font-bold">{stats.total_size_mb}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Storage Used (GB)</p>
            <p className="text-2xl font-bold">{stats.total_size_gb}</p>
          </div>
          <div className="bg-card border border-border rounded-lg p-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Provider</p>
            <p className="text-lg font-bold capitalize">{config.provider}</p>
          </div>
        </div>
      )}

      {/* Configuration Form */}
      <form onSubmit={saveConfig} className="bg-card border border-border rounded-lg p-6 space-y-4">
        <h3 className="font-semibold text-lg">Storage Configuration</h3>

        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Storage Provider</label>
          <select
            value={config.provider}
            onChange={(e) => setConfig({ ...config, provider: e.target.value })}
            className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
            data-testid="provider-select"
          >
            <option value="emergent">Emergent Object Storage (Default)</option>
            <option value="s3">Amazon S3</option>
            <option value="gcs">Google Cloud Storage</option>
            <option value="azure">Azure Blob Storage</option>
          </select>
        </div>

        {config.provider !== 'emergent' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Bucket Name</label>
                <input
                  value={config.bucket_name || ''}
                  onChange={(e) => setConfig({ ...config, bucket_name: e.target.value })}
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
                  data-testid="bucket-name"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Region</label>
                <input
                  value={config.region || ''}
                  onChange={(e) => setConfig({ ...config, region: e.target.value })}
                  placeholder="us-east-1"
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">Access Key</label>
              <input
                value={config.access_key || ''}
                onChange={(e) => setConfig({ ...config, access_key: e.target.value })}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded font-mono"
                data-testid="access-key"
              />
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-1 block">CDN URL (optional)</label>
              <input
                value={config.cdn_url || ''}
                onChange={(e) => setConfig({ ...config, cdn_url: e.target.value })}
                placeholder="https://cdn.example.com"
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded"
              />
            </div>
          </>
        )}

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
            id="enabled"
            data-testid="enabled-checkbox"
          />
          <label htmlFor="enabled" className="text-sm">Enable Storage</label>
        </div>

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 py-2.5 bg-primary text-primary-foreground rounded-lg font-semibold disabled:opacity-50"
            data-testid="save-btn"
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </button>
          <button
            type="button"
            onClick={testConnection}
            disabled={testing}
            className="px-4 py-2.5 border border-border rounded-lg font-semibold hover:bg-muted disabled:opacity-50"
            data-testid="test-btn"
          >
            {testing ? 'Testing...' : 'Test Connection'}
          </button>
        </div>
      </form>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800 font-semibold mb-2">ℹ️ Storage Provider Information</p>
        <ul className="text-xs text-blue-700 space-y-1">
          <li>• <strong>Emergent Object Storage</strong>: No configuration needed, works out of the box</li>
          <li>• <strong>Amazon S3</strong>: Requires AWS access keys and bucket name</li>
          <li>• <strong>Google Cloud Storage</strong>: Requires GCS credentials and bucket name</li>
          <li>• <strong>Azure Blob</strong>: Requires Azure storage account credentials</li>
        </ul>
      </div>
    </div>
  );
}
